import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Users, DollarSign, Check, X, Pause, Send, Trash2, Edit, RefreshCw, MessageSquare, Bell, Plus } from 'lucide-react';
import { toast } from 'sonner';

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const STATUS_CFG = {
  trial: { label: 'Trial', color: 'bg-zinc-700 text-zinc-300' },
  active: { label: 'Ativo', color: 'bg-green-500/20 text-green-400' },
  expired: { label: 'Expirado', color: 'bg-red-500/20 text-red-400' },
  paused: { label: 'Pausado', color: 'bg-yellow-500/20 text-yellow-400' },
};

export default function Admin() {
  const [tab, setTab] = useState('subscribers');
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({});
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planPrice, setPlanPrice] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [newNotif, setNewNotif] = useState({ titulo: '', texto: '' });
  const [addingNotif, setAddingNotif] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [subRes, statsRes, planRes, sugRes, notifRes] = await Promise.all([
        api.get('/admin/subscribers'),
        api.get('/admin/stats'),
        api.get('/subscriptions/plans/all'),
        api.get('/suggestions/admin').catch(() => ({ data: [] })),
        api.get('/notifications/admin/all').catch(() => ({ data: [] })),
      ]);
      setSubscribers(subRes.data);
      setStats(statsRes.data);
      setPlans(planRes.data);
      setSuggestions(sugRes.data);
      setNotifications(notifRes.data);
    } catch (err) {
      toast.error('Erro ao carregar dados admin');
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (userId, data) => {
    try {
      await api.put(`/admin/users/${userId}`, data);
      toast.success('Usuário atualizado');
      loadAll();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Excluir usuário permanentemente?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('Usuário excluído');
      loadAll();
    } catch { toast.error('Erro ao excluir'); }
  };

  const sendResetLink = async (userId) => {
    try {
      const res = await api.post(`/admin/users/${userId}/send-reset`);
      if (res.data.wpp_link) {
        window.open(res.data.wpp_link, '_blank');
      } else {
        toast.info('Link: ' + res.data.reset_link);
      }
      toast.success('Link de redefinição gerado');
    } catch { toast.error('Erro ao gerar link'); }
  };

  const savePlanPrice = async (planId) => {
    if (!planPrice) return;
    try {
      await api.put(`/subscriptions/plans/${planId}`, { preco_mensal: Number(planPrice) });
      toast.success('Preço atualizado');
      setEditingPlan(null);
      loadAll();
    } catch { toast.error('Erro ao atualizar plano'); }
  };

  const createNotification = async () => {
    if (!newNotif.titulo || !newNotif.texto) { toast.error('Preencha título e texto'); return; }
    setAddingNotif(true);
    try {
      await api.post('/notifications/admin', newNotif);
      toast.success('Notificação criada e publicada!');
      setNewNotif({ titulo: '', texto: '' });
      loadAll();
    } catch { toast.error('Erro ao criar notificação'); }
    finally { setAddingNotif(false); }
  };

  const deleteNotification = async (notifId) => {
    try {
      await api.delete(`/notifications/admin/${notifId}`);
      toast.success('Notificação removida');
      loadAll();
    } catch { toast.error('Erro ao remover notificação'); }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-white">Painel Admin</h1>
        <button onClick={loadAll} className="flex items-center gap-2 text-zinc-400 hover:text-white border border-zinc-700 px-3 py-1.5 rounded-lg transition-colors text-sm">
          <RefreshCw size={14} />
          Atualizar
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total usuários', value: stats.total_users || 0, color: 'text-white' },
          { label: 'Assinaturas ativas', value: stats.active_subscriptions || 0, color: 'text-green-400' },
          { label: 'Em trial', value: stats.trial_users || 0, color: 'text-yellow-400' },
          { label: 'Expirados', value: stats.expired_users || 0, color: 'text-red-400' },
        ].map(k => (
          <div key={k.label} className="stat-card">
            <div className="text-xs text-zinc-500 mb-1">{k.label}</div>
            <div className={`font-heading font-bold text-2xl ${k.color}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800 pb-0">
        {[
          { id: 'subscribers', label: 'Assinantes', icon: Users },
          { id: 'suggestions', label: `Sugestões (${suggestions.length})`, icon: MessageSquare },
          { id: 'notifications', label: 'Notificações', icon: Bell },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-orange-500 text-orange-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Subscribers tab */}
      {tab === 'subscribers' && (
        <>
          {/* Plans pricing */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
            <h2 className="font-heading font-semibold text-white mb-4">Planos e Preços</h2>
            <div className="space-y-3">
              {plans.map(plan => (
                <div key={plan.id} className="flex items-center justify-between bg-zinc-800/50 rounded-xl p-4">
                  <div>
                    <div className="font-medium text-white">{plan.nome}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{plan.features?.join(' · ')}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    {editingPlan === plan.id ? (
                      <>
                        <div className="flex items-center gap-1">
                          <span className="text-zinc-400 text-sm">R$</span>
                          <input type="number" step="0.01" className="w-24 field-input text-sm py-1.5 text-right"
                            value={planPrice} onChange={e => setPlanPrice(e.target.value)} />
                        </div>
                        <button onClick={() => savePlanPrice(plan.id)}
                          className="bg-orange-500 hover:bg-orange-400 text-white px-3 py-1.5 rounded-lg text-sm transition-colors">
                          Salvar
                        </button>
                        <button onClick={() => setEditingPlan(null)} className="text-zinc-500 hover:text-white transition-colors"><X size={16} /></button>
                      </>
                    ) : (
                      <>
                        <span className="font-mono font-bold text-orange-400">{brl(plan.preco_mensal)}/mês</span>
                        <button onClick={() => { setEditingPlan(plan.id); setPlanPrice(plan.preco_mensal); }}
                          className="text-zinc-400 hover:text-orange-400 transition-colors"><Edit size={15} /></button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Subscribers list */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-zinc-800">
              <h2 className="font-heading font-semibold text-white">Assinantes ({subscribers.length})</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-zinc-800">
                  <tr className="text-xs text-zinc-500">
                    <th className="text-left px-4 py-3">Usuário</th>
                    <th className="text-left px-4 py-3 hidden md:table-cell">Empresa</th>
                    <th className="text-left px-4 py-3 hidden lg:table-cell">Criado em</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Orç.</th>
                    <th className="text-right px-4 py-3 w-36">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/50">
                  {subscribers.map(u => {
                    const cfg = STATUS_CFG[u.subscription_status] || STATUS_CFG.trial;
                    const wpp = (u.whatsapp || '').replace(/\D/g, '');
                    return (
                      <tr key={u.id} className="table-row-hover">
                        <td className="px-4 py-3">
                          <div className="font-medium text-white">{u.nome}</div>
                          <div className="text-xs text-zinc-500">{u.email}</div>
                          {u.whatsapp && (
                            <a href={`https://wa.me/${wpp}`} target="_blank" rel="noopener noreferrer"
                              className="text-xs text-green-400 hover:text-green-300 flex items-center gap-1 mt-0.5">
                              {u.whatsapp}
                            </a>
                          )}
                        </td>
                        <td className="px-4 py-3 text-zinc-400 hidden md:table-cell">{u.empresa || '—'}</td>
                        <td className="px-4 py-3 text-zinc-400 text-xs hidden lg:table-cell">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`badge ${cfg.color}`}>{cfg.label}</span>
                          {!u.is_active && <span className="badge bg-red-500/20 text-red-400 ml-1">Suspenso</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-zinc-400">{u.proposals_count}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            {u.subscription_status !== 'active' && (
                              <button onClick={() => updateUser(u.id, { subscription_status: 'active', is_active: true, plan: 'pro' })}
                                title="Ativar" className="p-1.5 text-zinc-500 hover:text-green-400 transition-colors">
                                <Check size={14} />
                              </button>
                            )}
                            <button onClick={() => updateUser(u.id, { subscription_status: 'paused', is_active: false })}
                              title="Pausar" className="p-1.5 text-zinc-500 hover:text-yellow-400 transition-colors">
                              <Pause size={14} />
                            </button>
                            <button onClick={() => sendResetLink(u.id)}
                              title="Enviar reset de senha" className="p-1.5 text-zinc-500 hover:text-blue-400 transition-colors">
                              <Send size={14} />
                            </button>
                            <button onClick={() => deleteUser(u.id)}
                              title="Excluir" className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {subscribers.length === 0 && (
                <div className="text-center py-12 text-zinc-500 text-sm">Nenhum assinante cadastrado</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Suggestions tab */}
      {tab === 'suggestions' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-800">
            <h2 className="font-heading font-semibold text-white">Sugestões recebidas</h2>
          </div>
          {suggestions.length === 0 ? (
            <div className="text-center py-12 text-zinc-500 text-sm">Nenhuma sugestão recebida ainda</div>
          ) : (
            <div className="divide-y divide-zinc-800/50">
              {suggestions.map((s, idx) => (
                <div key={s.id || idx} className="p-5 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-zinc-500">{s.user_email || 'Anônimo'} · {s.created_at ? new Date(s.created_at).toLocaleDateString('pt-BR') : ''}</div>
                  </div>
                  <p className="text-sm text-zinc-300 whitespace-pre-line">{s.texto}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Notifications tab */}
      {tab === 'notifications' && (
        <div className="space-y-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
            <h2 className="font-heading font-semibold text-white">Criar nova notificação</h2>
            <div>
              <label className="field-label">Título</label>
              <input className="field-input" placeholder="Ex: Nova funcionalidade disponível!" value={newNotif.titulo} onChange={e => setNewNotif(p => ({ ...p, titulo: e.target.value }))} />
            </div>
            <div>
              <label className="field-label">Mensagem</label>
              <textarea className="field-input" rows={3} placeholder="Descreva a novidade ou recurso..." value={newNotif.texto} onChange={e => setNewNotif(p => ({ ...p, texto: e.target.value }))} />
            </div>
            <button onClick={createNotification} disabled={addingNotif}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-semibold px-4 py-2.5 rounded-xl transition-colors text-sm">
              <Bell size={15} />
              {addingNotif ? 'Publicando...' : 'Publicar notificação'}
            </button>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-zinc-800">
              <h2 className="font-heading font-semibold text-white">Notificações publicadas ({notifications.length})</h2>
            </div>
            {notifications.length === 0 ? (
              <div className="text-center py-10 text-zinc-500 text-sm">Nenhuma notificação publicada</div>
            ) : (
              <div className="divide-y divide-zinc-800/50">
                {notifications.map((n, idx) => (
                  <div key={n.id || idx} className="flex items-start gap-3 p-5">
                    <div className="flex-1 space-y-1">
                      <div className="font-medium text-white text-sm">{n.titulo}</div>
                      <p className="text-xs text-zinc-400">{n.texto}</p>
                      <div className="text-xs text-zinc-600">{n.created_at ? new Date(n.created_at).toLocaleDateString('pt-BR') : ''}</div>
                    </div>
                    <button onClick={() => deleteNotification(n.id)}
                      className="text-zinc-600 hover:text-red-400 transition-colors p-1">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
